import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Upload, X, Save, Eye, EyeOff, Calendar, Clock, Tag,
  Image as ImageIcon, FileText, AlertCircle, CheckCircle, Plus, Trash2,
  Building, Users, Target, TrendingUp, Award, DollarSign
} from 'lucide-react';

interface CaseStudyFormData {
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  clientName: string;
  clientIndustry: string;
  clientSize: string;
  projectDuration: string;
  projectBudget: string;
  challenge: string;
  solution: string;
  results: string;
  metrics: string[];
  testimonials: string[];
  featuredImage: string;
  status: 'draft' | 'published';
}

const CreateCaseStudy: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [newMetric, setNewMetric] = useState('');
  const [newTestimonial, setNewTestimonial] = useState('');
  const [errors, setErrors] = useState<Partial<CaseStudyFormData>>({});
  const [successMessage, setSuccessMessage] = useState('');

  const [formData, setFormData] = useState<CaseStudyFormData>({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    clientName: '',
    clientIndustry: '',
    clientSize: '',
    projectDuration: '',
    projectBudget: '',
    challenge: '',
    solution: '',
    results: '',
    metrics: [],
    testimonials: [],
    featuredImage: '',
    status: 'draft'
  });

  const industries = ['Technology', 'Healthcare', 'Finance', 'Manufacturing', 'Retail', 'Education', 'Government', 'Non-profit'];
  const clientSizes = ['Startup (1-50)', 'Small (51-200)', 'Medium (201-1000)', 'Large (1000+)', 'Enterprise (10000+)'];

  const handleInputChange = (field: keyof CaseStudyFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'title') {
      const slug = value.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').trim();
      setFormData(prev => ({ ...prev, slug }));
    }
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.size <= 5 * 1024 * 1024 && file.type.startsWith('image/')) {
      setUploadedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  const addMetric = () => {
    if (newMetric.trim() && !formData.metrics.includes(newMetric.trim())) {
      setFormData(prev => ({ ...prev, metrics: [...prev.metrics, newMetric.trim()] }));
      setNewMetric('');
    }
  };

  const addTestimonial = () => {
    if (newTestimonial.trim() && !formData.testimonials.includes(newTestimonial.trim())) {
      setFormData(prev => ({ ...prev, testimonials: [...prev.testimonials, newTestimonial.trim()] }));
      setNewTestimonial('');
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<CaseStudyFormData> = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.clientName.trim()) newErrors.clientName = 'Client name is required';
    if (!formData.challenge.trim()) newErrors.challenge = 'Challenge is required';
    if (!formData.solution.trim()) newErrors.solution = 'Solution is required';
    if (!formData.results.trim()) newErrors.results = 'Results are required';
    if (!uploadedImage) newErrors.featuredImage = 'Featured image is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const submitData = new FormData();
      submitData.append('image', uploadedImage!);
      
      Object.entries(formData).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          submitData.append(key, JSON.stringify(value));
        } else {
          submitData.append(key, value.toString());
        }
      });

      // TODO: Implement case study service
      console.log('Submitting case study:', submitData);
      
      setSuccessMessage('Case study created successfully!');
      setTimeout(() => navigate('/case-studies'), 2000);
    } catch (error) {
      console.error('Error creating case study:', error);
      setErrors({ content: 'Failed to create case study. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="container-custom">
          <div className="flex items-center justify-between h-16">
            <button onClick={() => navigate('/case-studies')} className="flex items-center space-x-2 text-gray-600 hover:text-gray-800">
              <ArrowLeft size={20} />
              <span>Back to Case Studies</span>
            </button>
            
            <div className="flex items-center space-x-3">
              <button onClick={() => setShowPreview(!showPreview)} className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200">
                {showPreview ? <EyeOff size={16} /> : <Eye size={16} />}
                <span>{showPreview ? 'Hide Preview' : 'Show Preview'}</span>
              </button>
              
              <button onClick={() => setFormData(prev => ({ ...prev, status: 'draft' }))} disabled={isLoading} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50">
                Save Draft
              </button>
              
              <button onClick={() => setFormData(prev => ({ ...prev, status: 'published' }))} disabled={isLoading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {isLoading ? 'Publishing...' : 'Publish'}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container-custom py-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className={`${showPreview ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-6">Create New Case Study</h1>

                {successMessage && (
                  <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="flex items-center space-x-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 mb-6">
                    <CheckCircle size={20} />
                    <span>{successMessage}</span>
                  </motion.div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Basic Information */}
                  <div className="space-y-4">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                      <FileText size={20} className="text-blue-600" />
                      <span>Basic Information</span>
                    </h2>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                      <input type="text" value={formData.title} onChange={(e) => handleInputChange('title', e.target.value)} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.title ? 'border-red-300' : 'border-gray-300'}`} placeholder="Case study title..." />
                      {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Excerpt</label>
                      <textarea value={formData.excerpt} onChange={(e) => handleInputChange('excerpt', e.target.value)} rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="Brief description..." />
                    </div>
                  </div>

                  {/* Client Information */}
                  <div className="space-y-4">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                      <Building size={20} className="text-green-600" />
                      <span>Client Information</span>
                    </h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Client Name *</label>
                        <input type="text" value={formData.clientName} onChange={(e) => handleInputChange('clientName', e.target.value)} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.clientName ? 'border-red-300' : 'border-gray-300'}`} placeholder="Client company name..." />
                        {errors.clientName && <p className="mt-1 text-sm text-red-600">{errors.clientName}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Industry</label>
                        <select value={formData.clientIndustry} onChange={(e) => handleInputChange('clientIndustry', e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                          <option value="">Select industry</option>
                          {industries.map(industry => <option key={industry} value={industry}>{industry}</option>)}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Company Size</label>
                        <select value={formData.clientSize} onChange={(e) => handleInputChange('clientSize', e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                          <option value="">Select size</option>
                          {clientSizes.map(size => <option key={size} value={size}>{size}</option>)}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Project Duration</label>
                        <input type="text" value={formData.projectDuration} onChange={(e) => handleInputChange('projectDuration', e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="e.g., 6 months" />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Project Budget</label>
                        <input type="text" value={formData.projectBudget} onChange={(e) => handleInputChange('projectBudget', e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="e.g., $100K - $500K" />
                      </div>
                    </div>
                  </div>

                  {/* Project Details */}
                  <div className="space-y-4">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                      <Target size={20} className="text-purple-600" />
                      <span>Project Details</span>
                    </h2>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Challenge *</label>
                      <textarea value={formData.challenge} onChange={(e) => handleInputChange('challenge', e.target.value)} rows={4} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.challenge ? 'border-red-300' : 'border-gray-300'}`} placeholder="Describe the client's challenge..." />
                      {errors.challenge && <p className="mt-1 text-sm text-red-600">{errors.challenge}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Solution *</label>
                      <textarea value={formData.solution} onChange={(e) => handleInputChange('solution', e.target.value)} rows={4} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.solution ? 'border-red-300' : 'border-gray-300'}`} placeholder="Describe your solution..." />
                      {errors.solution && <p className="mt-1 text-sm text-red-600">{errors.solution}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Results *</label>
                      <textarea value={formData.results} onChange={(e) => handleInputChange('results', e.target.value)} rows={4} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.results ? 'border-red-300' : 'border-gray-300'}`} placeholder="Describe the results and outcomes..." />
                      {errors.results && <p className="mt-1 text-sm text-red-600">{errors.results}</p>}
                    </div>
                  </div>

                  {/* Metrics & Testimonials */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                        <TrendingUp size={20} className="text-orange-600" />
                        <span>Key Metrics</span>
                      </h2>
                      
                      <div className="flex space-x-2 mb-2">
                        <input type="text" value={newMetric} onChange={(e) => setNewMetric(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addMetric())} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="Add a metric..." />
                        <button type="button" onClick={addMetric} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                          <Plus size={16} />
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {formData.metrics.map(metric => (
                          <span key={metric} className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                            <span>{metric}</span>
                            <button type="button" onClick={() => setFormData(prev => ({ ...prev, metrics: prev.metrics.filter(m => m !== metric) }))} className="text-blue-600 hover:text-blue-800">
                              <X size={14} />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                        <Award size={20} className="text-indigo-600" />
                        <span>Testimonials</span>
                      </h2>
                      
                      <div className="flex space-x-2 mb-2">
                        <input type="text" value={newTestimonial} onChange={(e) => setNewTestimonial(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTestimonial())} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="Add a testimonial..." />
                        <button type="button" onClick={addTestimonial} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                          <Plus size={16} />
                        </button>
                      </div>
                      <div className="space-y-2">
                        {formData.testimonials.map((testimonial, index) => (
                          <div key={index} className="flex items-center space-x-2 p-3 bg-indigo-50 rounded-lg">
                            <span className="flex-1 text-sm text-indigo-800">"{testimonial}"</span>
                            <button type="button" onClick={() => setFormData(prev => ({ ...prev, testimonials: prev.testimonials.filter((_, i) => i !== index) }))} className="text-indigo-600 hover:text-indigo-800">
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Content & Image */}
                  <div className="space-y-4">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                      <FileText size={20} className="text-teal-600" />
                      <span>Content & Media</span>
                    </h2>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Detailed Content</label>
                      <textarea value={formData.content} onChange={(e) => handleInputChange('content', e.target.value)} rows={8} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm" placeholder="Write detailed case study content... (HTML supported)" />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Featured Image *</label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                        {imagePreview ? (
                          <div className="space-y-4">
                            <img src={imagePreview} alt="Preview" className="max-w-full h-64 object-cover rounded-lg mx-auto" />
                            <button type="button" onClick={() => { setUploadedImage(null); setImagePreview(''); }} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                              <Trash2 size={16} />
                              <span>Remove Image</span>
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <Upload className="mx-auto h-12 w-12 text-gray-400" />
                            <button type="button" onClick={() => fileInputRef.current?.click()} className="text-blue-600 hover:text-blue-500 font-medium">
                              Click to upload
                            </button>
                            <p className="text-sm text-gray-500">PNG, JPG, GIF up to 5MB</p>
                          </div>
                        )}
                        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                      </div>
                      {errors.featuredImage && <p className="mt-1 text-sm text-red-600">{errors.featuredImage}</p>}
                    </div>
                  </div>
                </form>
              </motion.div>
            </div>

            {/* Preview Section */}
            {showPreview && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="lg:col-span-1">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-8">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Preview</h2>
                  
                  {formData.title && (
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-semibold text-gray-900">Title</h3>
                        <p className="text-gray-700">{formData.title}</p>
                      </div>
                      
                      {formData.clientName && (
                        <div>
                          <h3 className="font-semibold text-gray-900">Client</h3>
                          <p className="text-gray-700">{formData.clientName}</p>
                        </div>
                      )}
                      
                      {formData.challenge && (
                        <div>
                          <h3 className="font-semibold text-gray-900">Challenge</h3>
                          <p className="text-gray-700 text-sm">{formData.challenge.substring(0, 100)}...</p>
                        </div>
                      )}
                      
                      {formData.metrics.length > 0 && (
                        <div>
                          <h3 className="font-semibold text-gray-900">Metrics</h3>
                          <div className="flex flex-wrap gap-1">
                            {formData.metrics.map(metric => (
                              <span key={metric} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                                {metric}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {!formData.title && (
                    <p className="text-gray-500 text-center py-8">
                      Start filling out the form to see a preview here
                    </p>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCaseStudy;
